import { PlusOutlined, RobotOutlined } from '@ant-design/icons';
import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter, useIntl } from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';
import '@zhiyue/task-management/style.css';

import {
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { theme as antdTheme, Button, Tooltip } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  NotificationIcon,
  Question,
  SelectLang,
  ThemeToggle,
} from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
  SIDER_WIDTH_STORAGE_KEY,
} from '@/components/Layout/SiderResizeHandle';
import { translations } from '@/modules/task-management/config/i18n';
import {
  emitTaskNavQuickCreate,
  type TaskNavQuickCreateType,
} from '@/modules/task-management/navigation/quick-create-events';
import { taskNavStore } from '@/modules/task-management/navigation/task-nav-store';
import { createTranslator } from '@/modules/task-management/utils/i18n';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import {
  clearAutoRefresh,
  ensureValidAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import { getMyAuthorizedResources } from './services/bff/permission.api';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: (() => {
    try {
      return window.localStorage.getItem(SIDER_WIDTH_STORAGE_KEY)
        ? getStoredSiderWidth()
        : DEFAULT_SIDER_WIDTH;
    } catch {
      return DEFAULT_SIDER_WIDTH;
    }
  })(),
});

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(window.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle, workbenchUrl }) => {
  const intl = useIntl();
  const workbenchLabel = intl.formatMessage({ id: 'menu.account.workbench' });

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
      <button
        type="button"
        className="app-header-workbench-btn"
        onClick={() => {
          if (!workbenchUrl) {
            return;
          }
          window.open(workbenchUrl, '_self');
        }}
        disabled={!workbenchUrl}
        title={workbenchLabel}
      >
        <HomeOutlined />
        <span className="app-header-workbench-text">{workbenchLabel}</span>
      </button>
    </div>
  );
};

const AppThemeBridge: React.FC = () => {
  const { navTheme } = useTheme();
  const setAntdConfig = useAntdConfigSetter();
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const useDarkMode = navTheme === 'realDark';

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const themeMode = useDarkMode ? 'dark' : 'light';
    document.documentElement.dataset.appTheme = themeMode;
    document.documentElement.style.colorScheme = themeMode;
  }, [useDarkMode]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig, useDarkMode]);

  return null;
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

const SessionNotificationIcon: React.FC = () => {
  const { currentUser, loading } = useSession();
  const [token, setToken] = React.useState<string>();

  React.useEffect(() => {
    let active = true;

    if (loading || !currentUser) {
      setToken(undefined);
      return () => {
        active = false;
      };
    }

    void ensureValidAccessToken()
      .then((nextToken) => {
        if (active) {
          setToken(nextToken || undefined);
        }
      })
      .catch(() => {
        if (active) {
          setToken(undefined);
        }
      });

    return () => {
      active = false;
    };
  }, [currentUser, loading]);

  if (!token) {
    return null;
  }

  return <NotificationIcon mode="direct" token={token} />;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => {
  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <AppThemeBridge />
      <SessionSync setInitialState={setInitialState} />
      {children}
    </SessionProvider>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      currentTenantIsAdmin = tenantList.some(
        (tenant) =>
          tenant.id === currentTenantUuid && tenant.roleLabel === '管理员',
      );
    }
  }
  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(window.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const setCollapsed = (collapsed: boolean) => {
    setInitialState((previous) =>
      previous
        ? {
            ...previous,
            settings: { ...previous.settings, collapsed },
          }
        : previous,
    );
  };

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  const openQuickCreateModal = React.useCallback(
    (type: TaskNavQuickCreateType) => {
      emitTaskNavQuickCreate(type);
    },
    [],
  );

  const renderTaskCreateButtons = (
    collapsed: boolean,
    t: (key: string) => string,
  ) => (
    <div
      className={`task-nav-create-actions${collapsed ? ' task-nav-create-actions-collapsed' : ''}`}
    >
      {(['task', 'task-ai'] as const).map((type) => {
        const label = t(
          type === 'task' ? 'task_create_button_new_task' : 'ai_decompose_mode',
        );
        return (
          <Tooltip
            key={type}
            placement="right"
            title={collapsed ? label : undefined}
          >
            <button
              type="button"
              className={`task-nav-create-button${type === 'task-ai' ? ' task-nav-create-button-ai' : ''}${collapsed ? ' task-nav-create-button-collapsed' : ''}`}
              aria-label={label}
              onClick={() => openQuickCreateModal(type)}
            >
              {type === 'task' ? <PlusOutlined /> : <RobotOutlined />}
              {collapsed ? null : <span>{label}</span>}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );

  const renderTaskGroupTitle = React.useCallback(
    (
      item: { path?: string; key?: string; name?: string },
      dom: React.ReactNode,
    ) => {
      const groupPath = item.path || item.key;
      if (!groupPath) return dom;

      const groupTypeMap: Record<string, TaskNavQuickCreateType> = {
        '/task/task-lists/visible': 'task-list',
        '/task/tags/visible': 'tag',
        '/task/screen/all': 'screen',
      };
      const groupType = groupTypeMap[groupPath];
      if (!groupType) return dom;

      return (
        <div className="task-nav-group-title">
          <span className="task-nav-group-label">{dom}</span>
          <Button
            type="text"
            size="small"
            className="task-nav-group-add-btn"
            icon={<PlusOutlined />}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openQuickCreateModal(groupType);
            }}
          />
        </div>
      );
    },
    [openQuickCreateModal],
  );

  const attachAllItemCountLabel = React.useCallback((menus: any[]) => {
    const snapshot = taskNavStore.getSnapshot();
    const pendingCounts = snapshot.navPendingCounts;
    const countMap: Record<string, number> = {
      '/task/assignee': pendingCounts.assignee,
      '/task/today': pendingCounts.today,
      '/task/week': pendingCounts.week,
      '/task/next7Days': pendingCounts.next7Days,
      '/task/allTask': pendingCounts.allTask,
      '/task/create': pendingCounts.create,
      '/task/assigner': pendingCounts.assigner,
      '/task/watched': pendingCounts.watched,
      '/task/task-lists/visible': snapshot.taskLists.length,
      '/task/tags/visible': snapshot.tags.length,
      '/task/screen/all': snapshot.taskBoards.length,
    };

    return menus.map((item) => {
      const itemPath = item.path || item.key;
      const nextItem = { ...item };
      const count = countMap[itemPath];
      const shouldShowCount = typeof count === 'number' && count > 0;

      if (shouldShowCount) {
        nextItem.name = (
          <div className="task-nav-all-item">
            <span className="task-nav-all-item-label">{item.name}</span>
            <span className="task-nav-all-item-count">{count}</span>
          </div>
        );
      }

      if (Array.isArray(item.children) && item.children.length > 0) {
        nextItem.children = attachAllItemCountLabel(item.children);
      }

      return nextItem;
    });
  }, []);

  return {
    ...initialState?.settings,
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <SessionNotificationIcon key="notification" />,
        <Question key="doc" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return <></>;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      return showNav ? dom : false;
    },
    onCollapse: setCollapsed,
    collapsedButtonRender: (_collapsed, dom) =>
      typeof window !== 'undefined' && window.innerWidth <= 768 ? dom : false,
    menuExtraRender: (props) => {
      if (!getCurrentLayoutVisibility().showNav) return null;
      const collapsed = Boolean(props.collapsed);
      const t = createTranslator(translations, getStoredBrandLocale());
      const label = collapsed ? t('sidebar_expand') : t('sidebar_collapse');
      return (
        <div
          className={`task-nav-header${collapsed ? ' task-nav-header-collapsed' : ''}`}
        >
          <div className="task-nav-header-row">
            <Tooltip title={label} placement="right">
              <button
                type="button"
                className="task-nav-icon-button"
                aria-label={label}
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
            </Tooltip>
          </div>
          {renderTaskCreateButtons(collapsed, t)}
        </div>
      );
    },
    menuFooterRender: (props) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav) {
        return null;
      }

      const collapsed = Boolean((props as { collapsed?: boolean })?.collapsed);
      const locale = getStoredBrandLocale();
      const t = createTranslator(translations, locale);
      const label = t('common.settings');
      const updateLabel = t('sidebar_update');
      const pathname = window.location.pathname;
      const isSettingsRoute =
        pathname === '/task/settings' ||
        pathname === '/settings' ||
        pathname.startsWith('/task/settings/');

      const settingsButton = (
        <button
          type="button"
          className={`task-nav-footer-button${isSettingsRoute ? ' task-nav-footer-button-active' : ''}`}
          onClick={() => history.push('/task/settings')}
          aria-label={label}
          aria-current={isSettingsRoute ? 'page' : undefined}
        >
          <SettingOutlined />
          {collapsed ? null : <span>{label}</span>}
        </button>
      );
      const updateButton = (
        <button
          type="button"
          className="task-nav-footer-button"
          onClick={() => window.location.reload()}
          aria-label={updateLabel}
        >
          <ReloadOutlined />
          {collapsed ? null : <span>{updateLabel}</span>}
        </button>
      );

      return (
        <div
          className={`task-nav-footer${collapsed ? ' task-nav-footer-collapsed' : ''}`}
        >
          {collapsed ? (
            <Tooltip placement="right" title={updateLabel}>
              {updateButton}
            </Tooltip>
          ) : (
            updateButton
          )}
          {collapsed ? (
            <Tooltip placement="right" title={label}>
              {settingsButton}
            </Tooltip>
          ) : (
            settingsButton
          )}
        </div>
      );
    },
    menuDataRender: () => {
      const locale = getStoredBrandLocale();
      const t = createTranslator(translations, locale);
      const menuData = taskNavStore.toProLayoutMenuData(t);
      return attachAllItemCountLabel(menuData as any[]);
    },
    subMenuItemRender: (item, dom) => renderTaskGroupTitle(item, dom),
    headerTitleRender: (logo) => (
      <HeaderTitle
        logo={logo}
        appTitle={appTitle}
        workbenchUrl={workbenchUrl}
      />
    ),
    onPageChange: () => {
      const { pathname } = window.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          {children}
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
            onCollapse={() => setCollapsed(true)}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
